import { useCallback, useEffect, useMemo, useState } from 'react';
import { SerializedAccount } from 'fym-common/src/db/serializersTypes';
import { resolveSmartLinkPlatformDomain } from 'fym-shared/src/smartlinks/domains';
import { useUpdateAccountMutation } from '../../../../redux/api/fymAPI';

interface AddressValues {
  slug: string;
  platformDomain: string;
}

function initialValues(account: SerializedAccount | undefined): AddressValues {
  return {
    slug: account?.slug ?? '',
    // The picker always shows a real host. "Nothing chosen" is the default one.
    platformDomain: resolveSmartLinkPlatformDomain(null, account?.platformDomain),
  };
}

/**
 * The page address of the active artist as one draft (design artist-settings b1).
 *
 * Save sends the account PATCH with the address. The PATCH also carries the current avatar,
 * because `updateAccount` sends a missing avatar as null, and null removes it.
 */
export function useAddressForm(account: SerializedAccount | undefined) {
  const initial = useMemo(() => initialValues(account), [account]);
  const [values, setValues] = useState<AddressValues>(initial);
  const [updateAccount, { isLoading, error }] = useUpdateAccountMutation();

  useEffect(() => setValues(initial), [initial]);

  const setSlug = useCallback((slug: string) => setValues((current) => ({ ...current, slug })), []);
  const setPlatformDomain = useCallback(
    (platformDomain: string) => setValues((current) => ({ ...current, platformDomain })),
    []
  );
  const discard = useCallback(() => setValues(initial), [initial]);

  const isDirty = values.slug !== initial.slug || values.platformDomain !== initial.platformDomain;

  const save = useCallback(async (): Promise<void> => {
    if (account === undefined) {
      return;
    }
    await updateAccount({
      id: account.id,
      slug: values.slug,
      platformDomain: values.platformDomain,
      avatarFileId: account.avatarFile?.id,
    });
  }, [account, updateAccount, values.platformDomain, values.slug]);

  return {
    values,
    initial,
    setSlug,
    setPlatformDomain,
    discard,
    isDirty,
    save,
    isSaving: isLoading,
    error,
  };
}
